import React from "react";
import { v4 as uuid } from "uuid";

import { app } from "js/namespaces";
import logger from "js/core/logger";
import db from "js/db";
import { isPPTAddin } from "js/config";
import getLocalStorage from "js/core/utilities/localStorage";
import { ErrorMessageType } from "common/constants";
import { trackActivity } from "js/core/utilities/utilities";

const SESSION_ID_LOCAL_STORAGE_KEY = "bai-session";

const log = logger.info(logger.AUTH).bind(logger);

const localStorage = getLocalStorage();

/**
 * Tracks user session, makes sure user has only one active session
 */
export class FirebaseSessionTracker extends React.Component {
    constructor(props) {
        super(props);

        this.destroy = null;
    }

    async trackSession(user) {
        const { claims } = await user.getIdTokenResult();
        if (claims.session_tracking_disabled) {
            log(`[FirebaseSessionTracker] ${user.uid} user has the "session_tracking_disabled" custom claim set to true, won't be tracking session`);
            return;
        }

        log(`[FirebaseSessionTracker] app.integrator.type: ${app.integrator?.type}`);
        if (app.integrator) {
            log(`[FirebaseSessionTracker] ${user.uid} user - integrator skipping session tracking`);
            return;
        }

        let sessionId;
        const currentSessionId = localStorage.getItem(SESSION_ID_LOCAL_STORAGE_KEY);
        if (!currentSessionId) {
            sessionId = uuid();
            localStorage.setItem(SESSION_ID_LOCAL_STORAGE_KEY, sessionId);
            log(`[FirebaseSessionTracker] set new session ${sessionId}`);
        } else {
            sessionId = currentSessionId;
            log(`[FirebaseSessionTracker] using existing session ${sessionId}`);
        }

        let sessionType = "desktop";
        if (app.isMobileOrTablet) {
            sessionType = "mobile";
        } else if (isPPTAddin) {
            sessionType = "ppt-addin";
        }
        const sessionRef = db("sessions").child(user.uid).child(sessionType);

        // Saving session id
        await sessionRef.set(sessionId);

        // Listening to changes in the session object
        sessionRef.on("value", snap => {
            const newSessionId = snap.val();
            // A new tab was opened in the same browser
            if (newSessionId === sessionId) {
                return;
            }

            log(`[FirebaseSessionTracker] new session ${newSessionId} detected, logging out`);

            localStorage.removeItem(SESSION_ID_LOCAL_STORAGE_KEY);

            trackActivity("Auth", "DuplicateSessionLogOut", null, null, { workspace_id: "all" }, { audit: false });

            window.location = `/logout?continue=${encodeURIComponent(`/error/${ErrorMessageType.DUPLICATE_SESSION_ERROR}`)}`;
        });

        // We're going to need this to unmount correctly
        this.destroy = () => sessionRef.off();
    }

    componentWillUnmount() {
        if (this.destroy) {
            this.destroy();
        }
    }

    componentDidMount() {
        const { user } = this.props;

        this.trackSession(user)
            .catch(err => {
                logger.error("[FirebaseSessionTracker] trackSession() failed");
                logger.error(err);
            });
    }

    render() {
        const { children } = this.props;
        return <>{children}</>;
    }
}
